// ========================== src/pages/Perfil.jsx ==========================
import React, { useMemo } from "react";
import { FiUser } from "react-icons/fi";
import { useUserId } from "../UserIdContext";

export default function Perfil() {
  const { user } = useUserId();

  const currentUser = useMemo(() => {
    if (user) return user;
    if (typeof window === "undefined") return null;
    try {
      const raw =
        localStorage.getItem("usuario") || localStorage.getItem("binderUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [user]);

  const username = currentUser?.usuario || currentUser?._id || "Sin usuario";
  const role = currentUser?.rol || currentUser?.perfil || "Sin rol";
  const fullName =
    [currentUser?.nombre, currentUser?.apellido].filter(Boolean).join(" ") ||
    "No informado";
  const email = currentUser?.email || "";
  const ciudad = currentUser?.ciudad || "";
  const celular = currentUser?.celular || "";
  const legajo = currentUser?.legajo || "";
  const cuit = currentUser?.CUIT_CUIL || currentUser?.dni || "";
  const domicilio = currentUser?.domicilio || "";
  const hasUserData = !!currentUser;

  return (
    <div className="perfil-wrap">
      {/* ===== CSS hardcodeado en el mismo archivo ===== */}
      <style>{`
        :root{
          --bg:#0f1115;
          --panel:#131722;
          --panel-strong:#151a26;
          --stroke:#2a3142;
          --text:#e7eaf0;
          --muted:#aab1c3;
          --accent:#6c8cff;
          --success:#18c964;
        }

        .perfil-wrap{
          display:flex;
          flex-direction:column;
          gap:16px;
          color:var(--text);
        }

        .perfil-header .title-stack h1{
          margin:0;
          font-size:1.6rem;
          font-weight:700;
          display:flex;
          align-items:center;
          gap:10px;
        }
        .perfil-header .subtitle{
          margin:6px 0 0;
          color:var(--muted);
          font-size:0.95rem;
        }

        /* Panel solo para esta vista */
        .perfil-panel{
          background:var(--panel-strong);
          border:1px solid var(--stroke);
          border-radius:16px;
          padding:18px;
          box-shadow:0 10px 30px rgba(0,0,0,0.25);
        }

        .perfil-top{
          display:flex;
          align-items:center;
          gap:14px;
          padding-bottom:14px;
          border-bottom:1px dashed var(--stroke);
          margin-bottom:14px;
        }

        .avatar{
          position:relative;
          width:64px;height:64px;
          border-radius:16px;
          background:rgba(24,201,100,0.12);
          display:grid;place-items:center;
          border:1px solid rgba(24,201,100,0.35);
          overflow:hidden;
        }
        .avatar-ring{
          position:absolute;inset:-3px;
          border-radius:18px;
          border:2px solid rgba(24,201,100,0.45);
          filter:drop-shadow(0 0 6px rgba(24,201,100,0.25));
          animation:pulse 2.2s ease-in-out infinite;
        }
        @keyframes pulse{
          0%,100%{transform:scale(1);opacity:0.9}
          50%{transform:scale(1.05);opacity:0.6}
        }
        .avatar-icon{
          width:32px;height:32px;
          color:var(--success);
        }

        .perfil-id h2{
          margin:0 0 4px;
          font-size:1.2rem;
          font-weight:700;
        }

        .mono{
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        }
        .highlight{
          padding:2px 6px;
          background:#0f1a12;
          border:1px solid rgba(24,201,100,0.25);
          border-radius:8px;
        }

        .pill{
          display:inline-flex;align-items:center;gap:8px;
          font-size:0.85rem;padding:6px 10px;
          border-radius:999px;border:1px solid var(--stroke);
          color:var(--text);background:var(--panel);
        }
        .pill-success{
          border-color:rgba(24,201,100,0.4);
          box-shadow:inset 0 0 0 1px rgba(24,201,100,0.18);
        }

        .perfil-datos{
          display:grid;
          grid-template-columns:repeat(2, minmax(220px, 1fr));
          gap:12px 18px;
        }
        @media (max-width:720px){
          .perfil-datos{grid-template-columns:1fr;}
        }
        .dato{
          display:flex;gap:10px;align-items:baseline;
          padding:10px 12px;
          background:var(--panel);
          border:1px solid var(--stroke);
          border-radius:12px;
        }
        .label{color:var(--muted);min-width:110px;}
        .value{color:var(--text);font-weight:600;}
      `}</style>

      {/* Header */}
      <div className="perfil-header">
        <div className="title-stack">
          <h1>
            <FiUser /> Mi Perfil
          </h1>
          <p className="subtitle">Gestiona tu cuenta y datos personales</p>
        </div>
      </div>

      {/* Panel principal */}
      <div className="perfil-panel">
        <div className="perfil-top">
          <div className="avatar" aria-hidden="true">
            <div className="avatar-ring" />
            <FiUser className="avatar-icon" />
          </div>

          <div className="perfil-id">
            <h2 className="perfil-usuario">
              Usuario: <span className="mono highlight">{username}</span>
            </h2>
            <div className="perfil-line">
              <span className="pill pill-success" title="Perfil activo">
                {role}
              </span>
            </div>
          </div>
        </div>

        {!hasUserData && (
          <div className="dato" style={{ marginBottom: 12 }}>
            <span className="label">Estado:</span>
            <span className="value">No encontramos datos de usuario activos.</span>
          </div>
        )}

        {/* Datos */}
        <div className="perfil-datos">
          <div className="dato">
            <span className="label">Perfil:</span>
            <span className="value">{role}</span>
          </div>

          <div className="dato">
            <span className="label">Nombre:</span>
            <span className="value">{fullName}</span>
          </div>

          {email && (
            <div className="dato">
              <span className="label">Email:</span>
              <span className="value">{email}</span>
            </div>
          )}

          {ciudad && (
            <div className="dato">
              <span className="label">Ciudad:</span>
              <span className="value">{ciudad}</span>
            </div>
          )}

          {celular && (
            <div className="dato">
              <span className="label">Celular:</span>
              <span className="value">{celular}</span>
            </div>
          )}

          {legajo && (
            <div className="dato">
              <span className="label">Legajo:</span>
              <span className="value">{legajo}</span>
            </div>
          )}

          {cuit && (
            <div className="dato">
              <span className="label">Documento:</span>
              <span className="value">{cuit}</span>
            </div>
          )}

          {domicilio && (
            <div className="dato">
              <span className="label">Domicilio:</span>
              <span className="value">{domicilio}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
